/**
 * Image resizing / URL rewriting logic:
 * 1) Detect the maximum available bucket from the original filename prefix (e.g. `l_`, `m_`, `xs_`).
 * 2) Pick the smallest bucket in `IMAGE_SIZES` that is >= the requested `width`.
 * 3) If the requested bucket is larger than the maximum available bucket, cap to the maximum.
 * 4) Rewrite the filename to `<bucket>_<base>.webp` (e.g. `large_hero.webp`).
 *
 * Notes:
 * - If the source URL has no known prefix, we assume there are no resized assets and return `src` as-is.
 * - This function does not upscale beyond the detected max bucket; it caps to avoid requesting non-existent assets.
 */
export const IMAGE_SIZES = {
  xlarge: 3840,
  large: 1920,
  medium: 1280,
  small: 768,
  xsmall: 360,
  thumbnail: 180,
} as const;

export type ImageSize = keyof typeof IMAGE_SIZES;

const SIZE_TO_SHORT_PREFIX: Record<ImageSize, string> = {
  xlarge: 'xl',
  large: 'l',
  medium: 'm',
  small: 's',
  xsmall: 'xs',
  thumbnail: 't',
};

// Sort sizes ascending for finding target width
const SORTED_SIZES_ASC = Object.entries(IMAGE_SIZES).sort(
  ([, a], [, b]) => a - b,
) as [ImageSize, number][];

export const getMaxSizeFromUrl = (url: string): ImageSize | undefined => {
  const filename = url.split('/').pop();
  if (!filename) return undefined;

  for (const [size, prefix] of Object.entries(SIZE_TO_SHORT_PREFIX)) {
    if (filename.startsWith(`${prefix}_`)) {
      return size as ImageSize;
    }
  }
  return undefined;
};

export const getTargetSize = (width: number): ImageSize | undefined => {
  // Find the smallest size that is >= width
  const match = SORTED_SIZES_ASC.find(([, sizeWidth]) => sizeWidth >= width);
  // If width is larger than all sizes, return undefined
  return match ? match[0] : undefined;
};

export const getResponsiveUrl = (src: string, width: number): string => {
  if (!src) return '';

  const maxSize = getMaxSizeFromUrl(src);

  // If the original has no prefix, then there are no compressed pictures, return original
  if (!maxSize) {
    return src;
  }

  let finalSize = getTargetSize(width);

  if (!finalSize) {
    // Requested width is larger than all defined sizes, cap at maxSize
    finalSize = maxSize;
  } else {
    // If target size is larger than max available size, cap at max
    const maxWidth = IMAGE_SIZES[maxSize];
    const targetWidth = IMAGE_SIZES[finalSize];
    if (targetWidth > maxWidth) {
      finalSize = maxSize;
    }
  }

  // Construct new filename
  const parts = src.split('/');
  const filename = parts.pop();
  if (!filename) return src;

  let baseFilename = filename;
  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex !== -1) {
    baseFilename = filename.substring(0, lastDotIndex);
  }

  // Remove existing prefix from baseFilename
  // We know maxSize exists here
  const prefixToRemove = SIZE_TO_SHORT_PREFIX[maxSize];
  baseFilename = baseFilename.substring(prefixToRemove.length + 1);

  // Construct new name: size + "_" + base + ".webp"
  const newFilename = `${finalSize}_${baseFilename}.webp`;

  // Reassemble URL
  return [...parts, newFilename].join('/');
};
